import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { RegistrarPaqueteRequestSchema } from '@condo/contracts';
import type { RegistrarPaqueteRequest } from '@condo/contracts';

type RegistrarPaqueteFormValues = z.input<typeof RegistrarPaqueteRequestSchema>;
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useUnidades } from '@/features/unidades/api';
import { useRegistrarPaquete } from '@/lib/hooks/use-paquetes';
import { FotoUploader } from '@/components/paquetes/foto-uploader';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function NuevoPaqueteRoute() {
  const { condoId } = useCondoContext();
  const navigate = useNavigate();
  const { data: unidadesData, isLoading: unidadesLoading } = useUnidades(condoId);
  const mutation = useRegistrarPaquete();

  const form = useForm<RegistrarPaqueteFormValues, unknown, RegistrarPaqueteRequest>({
    resolver: zodResolver(RegistrarPaqueteRequestSchema),
    defaultValues: {
      unidadId: '',
      destinatarioNombre: '',
      descripcion: '',
      remitente: '',
      trackingNumber: '',
      fotoKeys: [],
    },
  });

  async function onSubmit(values: RegistrarPaqueteRequest) {
    try {
      await mutation.mutateAsync(values);
      toast.success('Paquete registrado', {
        description: `Paquete para ${values.destinatarioNombre} registrado exitosamente.`,
      });
      navigate(`/app/condominios/${condoId}/paquetes`);
    } catch {
      toast.error('No se pudo registrar el paquete');
    }
  }

  const unidades = unidadesData?.items ?? [];

  return (
    <PageContainer width="narrow">
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/paquetes`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>

      <PageHeader
        eyebrow="Nuevo paquete"
        title="Registrar paquete"
        subtitle="Ingresa los datos del paquete recibido en porteria."
        ruleSpacing="mb-8"
      />

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {/* Unidad */}
        <div>
          <Label htmlFor="unidadId">Unidad</Label>
          {unidadesLoading ? (
            <div className="mt-1.5 h-10 rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper-dim)] animate-pulse" />
          ) : (
            <Select
              value={form.watch('unidadId')}
              onValueChange={(v) => form.setValue('unidadId', v, { shouldValidate: true })}
            >
              <SelectTrigger id="unidadId" className="mt-1.5">
                <SelectValue placeholder="Selecciona una unidad" />
              </SelectTrigger>
              <SelectContent>
                {unidades.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.numero}
                    {u.piso ? ` · Piso ${u.piso}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {form.formState.errors.unidadId && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.unidadId.message}
            </p>
          )}
        </div>

        {/* Destinatario */}
        <div>
          <Label htmlFor="destinatarioNombre">Nombre del destinatario</Label>
          <Input
            id="destinatarioNombre"
            className="mt-1.5"
            placeholder="Ej: Juan Perez"
            {...form.register('destinatarioNombre')}
          />
          {form.formState.errors.destinatarioNombre && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.destinatarioNombre.message}
            </p>
          )}
        </div>

        {/* Remitente */}
        <div>
          <Label htmlFor="remitente">Remitente (opcional)</Label>
          <Input
            id="remitente"
            className="mt-1.5"
            placeholder="Ej: Amazon, Falabella"
            {...form.register('remitente')}
          />
        </div>

        {/* Tracking */}
        <div>
          <Label htmlFor="trackingNumber">Numero de seguimiento (opcional)</Label>
          <Input
            id="trackingNumber"
            className="mt-1.5 font-mono"
            placeholder="Ej: CL123456789"
            {...form.register('trackingNumber')}
          />
        </div>

        {/* Descripcion */}
        <div>
          <Label htmlFor="descripcion">Descripcion (opcional)</Label>
          <textarea
            id="descripcion"
            rows={3}
            className="mt-1.5 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)] disabled:opacity-60"
            placeholder="Ej: Caja mediana, sobre"
            {...form.register('descripcion')}
          />
        </div>

        {/* Fotos */}
        <div>
          <Label>Fotos (opcional, max 5)</Label>
          <div className="mt-1.5">
            <FotoUploader
              onChange={(keys) => form.setValue('fotoKeys', keys, { shouldValidate: true })}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-hairline)] pt-5">
          <Button variant="ghost" asChild>
            <Link to={`/app/condominios/${condoId}/paquetes`}>Cancelar</Link>
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Registrando…
              </>
            ) : (
              'Registrar paquete'
            )}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
